"use client";

import { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { useDataFetch } from "../../../hooks/use-data-fetch";

// Espera por defecto cuando el backend no la informa (primer reenvio).
const DEFAULT_COOLDOWN_SECONDS = 120;

const SUPPORT_MESSAGE =
  "Ya realizaste 3 intentos de reenvío. Por favor comunícate con soporte técnico para activar tu cuenta.";

const MESSAGES: Record<string, { text: string; variant: "success" | "warning" | "danger" }> = {
  confirmation_email_resend_success: {
    text: "Te reenviamos el correo de verificación. Revisa tu bandeja de entrada y la carpeta de spam.",
    variant: "success",
  },
  confirmation_email_already_confirmed: {
    text: "Su correo se encuentra autenticado con éxito",
    variant: "success",
  },
  confirmation_email_resend_too_soon: {
    text: "Aún no puedes solicitar otro correo. Espera a que termine la cuenta regresiva.",
    variant: "warning",
  },
  confirmation_email_resend_limit_reached: {
    text: SUPPORT_MESSAGE,
    variant: "danger",
  },
};

interface ResendConfirmationEmailProps {
  /** Si se conoce el correo se muestra solo el botón; si no, se pide al usuario. */
  email?: string;
  /** Segundos de espera ya impuestos por el servidor (p. ej. tras el login). */
  initialCooldown?: number;
  /** true cuando el servidor ya agotó los 3 intentos. */
  initialLimitReached?: boolean;
}

const ResendConfirmationEmail = ({
  email,
  initialCooldown = 0,
  initialLimitReached = false,
}: ResendConfirmationEmailProps) => {
  const [inputEmail, setInputEmail] = useState(email ?? "");
  const [isSending, setIsSending] = useState(false);
  const [feedback, setFeedback] = useState<{ text: string; variant: string } | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [limitReached, setLimitReached] = useState(initialLimitReached);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { postData } = useDataFetch();
  const { mutateAsync: resend } = postData("/users/resend-confirmation", {});

  const startCooldown = (seconds: number) => {
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    if (seconds <= 0) return setCooldown(0);

    setCooldown(seconds);
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // El servidor manda la espera vigente (2, 5 o 10 min según los intentos).
  useEffect(() => {
    if (initialCooldown > 0) startCooldown(initialCooldown);
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCooldown]);

  useEffect(() => {
    setLimitReached(initialLimitReached);
  }, [initialLimitReached]);

  const handleResend = async () => {
    const target = (email ?? inputEmail).trim();

    if (!target) {
      setFeedback({ text: "Ingresa tu correo electrónico.", variant: "danger" });
      return;
    }

    try {
      setIsSending(true);
      setFeedback(null);

      const response = await resend({ email: target });

      setFeedback(
        MESSAGES[response?.message] ?? MESSAGES.confirmation_email_resend_success,
      );
      startCooldown(response?.data?.retry_after_seconds ?? DEFAULT_COOLDOWN_SECONDS);

      if (response?.data?.attempts_left === 0) setLimitReached(true);
    } catch (err: any) {
      const known = MESSAGES[err?.message];

      setFeedback(
        known ?? { text: "No pudimos reenviar el correo. Intenta nuevamente.", variant: "danger" },
      );

      if (err?.message === "confirmation_email_resend_limit_reached") {
        setLimitReached(true);
      } else if (err?.message !== "confirmation_email_already_confirmed") {
        // Ya confirmado: no tiene sentido reintentar, no se arranca espera.
        startCooldown(err?.data?.retry_after_seconds ?? DEFAULT_COOLDOWN_SECONDS);
      }
    } finally {
      setIsSending(false);
    }
  };

  if (limitReached) {
    return (
      <Alert variant="danger" className="mb-0 py-2">
        <i className="ri-customer-service-2-line me-2" />
        {SUPPORT_MESSAGE}
      </Alert>
    );
  }

  return (
    <div>
      {!email && (
        <Form.Group className="mb-2">
          <Form.Control
            type="email"
            placeholder="tucorreo@ejemplo.com"
            value={inputEmail}
            onChange={(e) => setInputEmail(e.target.value)}
            disabled={isSending}
          />
        </Form.Group>
      )}

      <Button
        variant="link"
        className="text-primary p-0 fw-medium"
        onClick={handleResend}
        disabled={isSending || cooldown > 0}
      >
        {isSending ? (
          <>
            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
            <span className="ms-2">Reenviando...</span>
          </>
        ) : cooldown > 0 ? (
          `Reenviar en ${Math.floor(cooldown / 60)}:${String(cooldown % 60).padStart(2, "0")}`
        ) : (
          "¿No recibiste el correo? Reenviar"
        )}
      </Button>

      {feedback && (
        <Alert variant={feedback.variant} className="mt-3 mb-0 py-2">
          {feedback.text}
        </Alert>
      )}
    </div>
  );
};

export default ResendConfirmationEmail;
